import PropTypes from 'prop-types';
import _ from 'underscore';

export const NodeState = {
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
};

export const Node = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  state: PropTypes.shape(NodeState),
  isExpandable: PropTypes.bool,
  elName: PropTypes.string,
};

Node.children = PropTypes.arrayOf(PropTypes.shape(Node));

export const NodeProps = PropTypes.shape(Node);

export const FlattenedNode = _.extend(Node, {
  deepness: PropTypes.number,
  parents: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
});

export const Renderer = {
  node: PropTypes.shape(FlattenedNode),
  onChange: PropTypes.func.isRequired,
  nodeChildRenderer: PropTypes.func.isRequired,
};

export const IconsClassMapShape = PropTypes.shape({
  expanded: PropTypes.string,
  collapsed: PropTypes.string,
  lastChild: PropTypes.string,
});

export const TreeViewProps = {
  /**
   * Array of nodes to be displayed in the tree view component
   */
  nodes: PropTypes.arrayOf(NodeProps).isRequired,
  /**
   * function to render nodes
   * @param {NodeProps} - node to be rendered
   * @param {onChange} - callback to be trigerred when the node state is modified
   * @param {children} - child html to be rendered inside the expandable control
   * @param {getClassNames} - function to get list of class names for a specific node
     combined with default class names
   * @param {iconsClassNameMap} - mapping of icons to class names used to render a tree
     node passed from the parent component
   */
  nodeRenderer: PropTypes.func,
  /**
   * function to render node children
   * @param {NodeProps} - node to be rendered
   */
  nodeChildRenderer: PropTypes.func,
  /**
   * mapping of event types and function handlers
   */
  updateTypeHandlers: PropTypes.objectOf(PropTypes.func),
  /**
   * list of space separated class names for the tree view
   */
  treeViewClassNames: PropTypes.string,
  /**
   * function to get list of class names for a specific node
   * @param {NodeProps} - node for which classnames is to be fetched
   */
  getClassNames: PropTypes.func,
  /**
   * mapping of icons to class names used to render a tree node
   */
  iconsClassNameMap: PropTypes.instanceOf(Object),
  /**
   * callback function called after node state update handler is trigerred
   * @param {PropTypes.arrayOf(NodeProps)} - list of updated nodes
   */
  onChange: PropTypes.func.isRequired,
  /**
   * function to filter the list of nodes to be displayed
   */
  filterNode: PropTypes.func,
  /**
   * function to handle treeview will un-mount event
   */
  onTreeViewWillUnmount: PropTypes.func,
  /**
   * function to handle treeview did mount event
   */
  onTreeViewDidMount: PropTypes.func,
  /**
   * minimum height of each node
   */
  cellHeight: PropTypes.number,
  /**
   * max height of tree, when using autoHeight
   */
  maxHeight: PropTypes.number,
  /**
   * boolean to calculate the height of the veiw based on number of nodes and cellHeight
   */
  autoHeight: PropTypes.bool,
  /**
  * padding at each levels
  */
  nodePaddingLeft: PropTypes.number,
  /**
   * identifier of the node to scroll the tree view to on rendering
   */
  scrollToId: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  /**
   * indicate if Loading Rows on Scroll is enabled
   */
  enableOnScrollLoad: PropTypes.bool,
  onScrollOptions: PropTypes.shape({
    /**
     * Threshold at which to pre-fetch data. A threshold X means that data will start
     * loading when a user scrolls within X rows. Defaults to 15
    */
    threshold: PropTypes.number,
    /**
    * minimum batch size of rows to trigger the on Scroll event
    */
    batchSize: PropTypes.number,
  }),
  /**
   * used when enableOnScrollLoad is enabled
   * ({ index }) function to determine if more rows need to be fetched
   * when the scroll reaches the index in the tree view
   */
  isRowLoaded: PropTypes.func,
  /**
   * used when enableOnScrollLoad is enabled
   * ({ startIndex, stopIndex })
   * async callback to fetch more rows when scrolled if isRowLoaded returns false
   */
  loadMoreRows: PropTypes.func,
  /**
   * aria label of expandable node
   */
  expandIconAriaLabel: PropTypes.string,
  /**
   * Pre-set row count
   */
  rowCount: PropTypes.number,
  /**
   * aria label of grid
   */
  ariaLabel: PropTypes.string,
  /**
   * should clear cell measurer cache when tree view is updated
   */
  shouldClearCellMeasurerCache: PropTypes.bool,
  /**
   * placeholder to be displayed when there are no nodes to be displayed
   */
  placeholder: PropTypes.node,
};

export const VirtualizedTreeViewProps = {
  /**
   * Array of nodes to be displayed in the tree view component
   */
  nodes: PropTypes.arrayOf(NodeProps).isRequired,
  /**
   * function to render nodes
   * @param {NodeProps} - node to be rendered
   * @param {onChange} - callback to be trigerred when the node state is modified
   * @param {children} - child html to be rendered inside the expandable control
   * @param {getClassNames} - function to get list of class names for a specific node
     combined with default class names
   * @param {iconsClassNameMap} - mapping of icons to class names used to render a tree
     node passed from the parent component
   */
  children: PropTypes.func.isRequired,
  /**
   * callback function called after node state update handler is trigerred
   * @param {PropTypes.arrayOf(NodeProps)} - list of updated nodes
   */
  onChange: PropTypes.func.isRequired,
  /**
   * function to filter the list of nodes to be displayed
   */
  filterNode: PropTypes.func.isRequired,
  /**
   * used to enable or customize deep filtering of nodes
   */
  filterNodeDeep: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  /**
   * mapping of event types and function handlers
   */
  extensions: PropTypes.shape({
    updateTypeHandlers: PropTypes.objectOf(PropTypes.func),
  }),
  /**
   * minimum height of each node
   */
  cellHeight: PropTypes.number.isRequired,
  /**
   * boolean to calculate the height of the veiw based on number of nodes and cellHeight
   */
  autoHeight: PropTypes.bool.isRequired,
  /**
   * list of space separated class names for the tree view
   */
  treeViewClassNames: PropTypes.string.isRequired,
  /**
   * identifier of the node to scroll the tree view to on rendering
   */
  scrollToId: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  /**
   * left padding of each node at various levels
   */
  nodePaddingLeft: PropTypes.number.isRequired,
  /**
   * indicate if Loading Rows on Scroll is enabled
   */
  enableOnScrollLoad: PropTypes.bool.isRequired,
  onScrollOptions: PropTypes.shape({
    /**
     * Threshold at which to pre-fetch data. A threshold X means that data will start
     * loading when a user scrolls within X rows. Defaults to 15
    */
    threshold: PropTypes.number,
    /**
    * minimum batch size of rows to trigger the on Scroll event
    */
    batchSize: PropTypes.number,
  }),
  /**
   * used when enableOnScrollLoad is enabled
   * ({ index }) function to determine if more rows need to be fetched
   * when the scroll reaches the index in the tree view
   */
  isRowLoaded: PropTypes.func.isRequired,
  /**
   * used when enableOnScrollLoad is enabled
   * ({ startIndex, stopIndex })
   * async callback to fetch more rows when scrolled if isRowLoaded returns false
   */
  loadMoreRows: PropTypes.func.isRequired,

  /**
   * used when enableOnScrollLoad is enabled
   * Pre-determined number of rows in list.
   */
  rowCount: PropTypes.number,

  getFlattenedTree: PropTypes.func,

  /**
   * function to get list of class names for a specific node
   * @param {NodeProps} - node for which classnames is to be fetched
   */
  getClassNames: PropTypes.func.isRequired,

  /**
   * aria label of grid
   */
  ariaLabel: PropTypes.string,

  /**
   * should clear cell measurer cache when tree view is updated
   */
  shouldClearCellMeasurerCache: PropTypes.bool,
};

export const ExpandableNodeShape = {
  node: PropTypes.shape(FlattenedNode).isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  singleClickExpansion: PropTypes.bool,
  getClassNames: PropTypes.func,
  iconsClassNameMap: IconsClassMapShape,
  expandIconAriaLabel: PropTypes.string,
};
